import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Scale, Footprints, TrendingDown, TrendingUp, Trash2, Target } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";

const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "America/Panama" });
const todayPanama = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Panama" });

// Static chart props kept module-level to avoid re-creating objects on every render.
const WCHART_MARGIN = { top: 5, right: 10, left: -20, bottom: 0 };
const SCHART_MARGIN = { top: 5, right: 10, left: -10, bottom: 0 };
const AXIS_TICK = { fontSize: 11 };
const TOOLTIP_STYLE = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 };
const BAR_CURSOR = { fill: "hsl(var(--accent))" };
const LINE_DOT = { r: 3 };
const BAR_RADIUS = [6, 6, 0, 0];

export function Progress() {
  const { tl } = useI18n();
  const [weights, setWeights] = useState([]);
  const [steps, setSteps] = useState([]);
  const [goals, setGoals] = useState({ weight_goal: null, steps_goal: null });
  const [w, setW] = useState("");
  const [s, setS] = useState("");

  const loadW = () => api.get("/me/measurements").then(({ data }) => setWeights(data));
  const loadS = () => api.get("/me/steps").then(({ data }) => setSteps(data));
  useEffect(() => { loadW(); loadS(); api.get("/me/goals").then(({ data }) => setGoals(data)).catch(() => {}); }, []);

  const saveWeight = async () => {
    const val = parseFloat(w);
    if (!val || val <= 0) return;
    try { await api.post("/me/measurements", { weight: val }); setW(""); loadW();
      toast.success(tl({ es: "Peso registrado", en: "Weight saved", fr: "Poids enregistré" })); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delWeight = async (id) => { try { await api.delete(`/me/measurements/${id}`); loadW(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const saveSteps = async () => {
    const val = parseInt(s, 10);
    if (isNaN(val) || val < 0) return;
    try { await api.post("/me/steps", { steps: val }); setS("");
      const { data } = await api.get("/me/steps"); setSteps(data);
      toast.success(tl({ es: "Pasos registrados", en: "Steps saved", fr: "Pas enregistrés" })); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const wChart = weights.map((m) => ({ date: fmtDay(m.date), poids: m.weight }));
  const latest = weights.length ? weights[weights.length - 1] : null;
  const prev = weights.length > 1 ? weights[weights.length - 2] : null;
  const delta = latest && prev ? +(latest.weight - prev.weight).toFixed(1) : null;
  const wVals = weights.map((m) => m.weight).concat(goals.weight_goal ? [goals.weight_goal] : []);
  const wDomain = wVals.length ? [Math.floor(Math.min(...wVals) - 2), Math.ceil(Math.max(...wVals) + 2)] : [0, 100];

  const stepChart = steps.slice(-14).map((x) => ({ date: fmtDay(x.date), pas: x.steps }));
  const weekTotal = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 7);
    return steps.filter((x) => new Date(`${x.date}T12:00:00`) >= cutoff).reduce((a, b) => a + (b.steps || 0), 0);
  }, [steps]);
  const todayVal = steps.find((x) => x.date === todayPanama())?.steps || 0;

  return (
    <AppLayout>
      <h1 className="mb-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
        <Scale className="h-7 w-7 text-primary" /> {tl({ es: "Mi progreso", en: "My progress", fr: "Mon suivi" })}
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">{tl({ es: "Registra tu peso y tus pasos para seguir tu evolución.", en: "Log your weight and steps to track your evolution.", fr: "Enregistre ton poids et tes pas pour suivre ton évolution." })}</p>

      {/* WEIGHT */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="weight-card">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-display text-lg font-bold"><Scale className="h-5 w-5" /> {tl({ es: "Peso", en: "Weight", fr: "Poids" })}</h2>
            {latest && (
              <div className="mt-1 flex items-center gap-2">
                <span className="font-display text-2xl font-bold">{latest.weight} kg</span>
                {delta !== null && delta !== 0 && (
                  <span className={`flex items-center gap-1 text-sm font-semibold ${delta < 0 ? "text-primary" : "text-amber-500"}`}>
                    {delta < 0 ? <TrendingDown className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}{Math.abs(delta)} kg
                  </span>
                )}
              </div>
            )}
            {goals.weight_goal && (
              <div data-testid="weight-goal-label" className="mt-1 flex items-center gap-1 text-xs font-medium text-gold"><Target className="h-3.5 w-3.5" /> {tl({ es: "Objetivo", en: "Goal", fr: "Objectif" })}: {goals.weight_goal} kg</div>
            )}
          </div>
          <div className="flex items-end gap-2">
            <Input data-testid="weight-input" type="number" step="0.1" value={w} onChange={(e) => setW(e.target.value)}
              placeholder="72.5" className="h-11 w-28" />
            <Button data-testid="weight-save" onClick={saveWeight} disabled={!w} className="h-11 rounded-full">{tl({ es: "Guardar", en: "Save", fr: "Enregistrer" })}</Button>
          </div>
        </div>
        {wChart.length > 1 ? (
          <div className="h-56 w-full" data-testid="weight-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={wChart} margin={WCHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={AXIS_TICK} stroke="hsl(var(--muted-foreground))" />
                <YAxis domain={wDomain} tick={AXIS_TICK} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                {goals.weight_goal && <ReferenceLine y={goals.weight_goal} stroke="hsl(var(--gold, 40 90% 50%))" strokeDasharray="5 4" strokeWidth={1.5} label={{ value: `🎯 ${goals.weight_goal}kg`, position: "insideTopRight", fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />}
                <Line type="monotone" dataKey="poids" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={LINE_DOT} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{tl({ es: "Registra al menos 2 pesos para ver la curva.", en: "Log at least 2 weigh-ins to see the chart.", fr: "Enregistre au moins 2 pesées pour voir la courbe." })}</p>
        )}
        {weights.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {[...weights].reverse().slice(0, 8).map((m) => (
              <span key={m.measurement_id} data-testid={`weight-item-${m.measurement_id}`} className="group flex items-center gap-2 rounded-full bg-secondary/60 px-3 py-1 text-xs">
                {fmtDay(m.date)} · <b>{m.weight} kg</b>
                <button onClick={() => delWeight(m.measurement_id)} className="text-destructive opacity-60 hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* STEPS */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="steps-card">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-display text-lg font-bold"><Footprints className="h-5 w-5" /> {tl({ es: "Pasos", en: "Steps", fr: "Pas" })}</h2>
            <div className="mt-1 flex items-center gap-4 text-sm">
              <span><b className="font-display text-2xl">{todayVal.toLocaleString()}</b> {tl({ es: "hoy", en: "today", fr: "aujourd'hui" })}</span>
              <span className="text-muted-foreground">{tl({ es: "Semana", en: "Week", fr: "Semaine" })}: <b>{weekTotal.toLocaleString()}</b></span>
              {goals.steps_goal ? <span data-testid="steps-goal-label" className="flex items-center gap-1 font-medium text-gold"><Target className="h-3.5 w-3.5" /> {goals.steps_goal.toLocaleString()}</span> : null}
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Input data-testid="steps-input" type="number" value={s} onChange={(e) => setS(e.target.value)}
              placeholder="8000" className="h-11 w-28" />
            <Button data-testid="steps-save" onClick={saveSteps} disabled={s === ""} className="h-11 rounded-full">{tl({ es: "Guardar", en: "Save", fr: "Enregistrer" })}</Button>
          </div>
        </div>
        {stepChart.length > 0 ? (
          <div className="h-56 w-full" data-testid="steps-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stepChart} margin={SCHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={AXIS_TICK} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={AXIS_TICK} stroke="hsl(var(--muted-foreground))" />
                <Tooltip cursor={BAR_CURSOR} contentStyle={TOOLTIP_STYLE} />
                {goals.steps_goal ? <ReferenceLine y={goals.steps_goal} stroke="hsl(var(--gold, 40 90% 50%))" strokeDasharray="5 4" strokeWidth={1.5} label={{ value: `🎯 ${goals.steps_goal}`, position: "insideTopRight", fontSize: 10, fill: "hsl(var(--muted-foreground))" }} /> : null}
                <Bar dataKey="pas" fill="hsl(var(--primary))" radius={BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{tl({ es: "Registra tus pasos diarios.", en: "Log your daily steps.", fr: "Enregistre tes pas quotidiens." })}</p>
        )}
      </div>
    </AppLayout>
  );
}
